import React from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import PropertyDetailPopup from 'app/Property/Detail/Detail';
import { getPropertyPhoto } from '../propertyPhoto';
import { formatPropertyAddress, formatPropertyFacts } from 'data/investors';
import css from './style.scss';

// "Owned Properties" block — Figma node I34:3141;37:15025.
//
// Shows one property with a "+ N more" pill, from the properties grouped onto
// each investor by data/investors/investorUtils. The response lists every
// investor tied to a comparable in `property.investors`; the transform resolves
// each comparable to a single investor, orders that investor's properties
// currently-owned first, and carries the resolved `currentOwner` flag onto each.
// So the first entry here is the investor's current property when they have one,
// otherwise any one property they are tied to in the area.
//
// The search response now carries a photo (`imageUrl`) and PropStream's own
// `propertyId` per comparable, so the thumbnail is a real photo and the block
// opens Property Details — both of which this card previously had no source for.
// Properties without a photo fall back to the property search's own no-photo
// tile so the two surfaces show the same thing.

const OwnedProperties = ({ properties, currentCount, onViewMore }) => {
  const dispatch = useDispatch();

  if (!properties || !properties.length) return null;

  const [first] = properties;
  if (!formatPropertyAddress(first)) return null;
  const remaining = properties.length - 1;
  const facts = formatPropertyFacts(first);
  const photo = getPropertyPhoto(first);

  // The middleware puts currently-owned properties first, so a zero current
  // count means everything here is a past transaction in the area — calling
  // those "Owned" would misstate the data.
  const title = currentCount > 0 ? 'Owned Properties' : 'Properties in This Area';

  // Property Details is opened as a popup with `preventRouting`, not a route
  // push: a route change would navigate away from the search underneath and
  // lose the results the user just ran. Keyed on PropStream's `propertyId` —
  // `batchDataPropertyId` is a vendor id Property Details cannot look up, and
  // the service leaves `propertyId` null for a comparable it could not match,
  // which is why this stays a plain div in that case rather than a dead button.
  const openDetails = () =>
    dispatch(
      PropertyDetailPopup.open({
        propertyId: first.propertyId,
        preventRouting: true,
        match: { params: { id: first.propertyId } },
      })
    );

  const media = (
    <>
      <img className={css.preview__thumb} src={photo} alt="" />
      {/* Spans, not paragraphs: this subtree is rendered inside a <button>
          when the property is clickable, and a button may only contain
          phrasing content. They are flex items either way, so they still lay
          out as their own lines without a style change. */}
      <span className={css.preview__text}>
        <span className={css.preview__address}>{formatPropertyAddress(first)}</span>
        {!facts ? null : <span className={css.preview__facts}>{facts}</span>}
      </span>
    </>
  );

  return (
    <div className={css.owned}>
      <p className={css.owned__title}>{title}</p>
      <div className={css.preview}>
        {!first.propertyId ? (
          <div className={css.preview__media}>{media}</div>
        ) : (
          <button
            type="button"
            className={classNames(css.preview__media, css.preview__mediaButton)}
            onClick={openDetails}
          >
            {media}
          </button>
        )}
        {/* Figma 34:3859 labels this pill "View More", not a count — it opens the
            investor profile, whose Linked Properties table is the one place
            that lists every property. Still hidden when this is the only one:
            the pill sits in the Owned Properties block, so it promises more
            properties rather than more investor detail. `onViewMore` is passed
            down rather than dispatched here so the card keeps owning which
            modal it opens, the same way its View Details button does. */}
        {remaining <= 0 || !onViewMore ? null : (
          <button type="button" className={css.preview__more} onClick={onViewMore}>
            View More
          </button>
        )}
      </div>
    </div>
  );
};

export default OwnedProperties;
